/**
 * 應收帳款明細表勾「依客戶分別列印」時的版面(REQ-20260901013)。
 *
 * 使用者拿四張圖來:單獨查一位客戶印出來的樣子(圖二 / 圖四)資訊完整,
 * 但查日期區間 + 勾分組印出來(圖三)是另一套 —— 少了公司抬頭、帳款區間、
 * 頁數分母,表身只有七欄,尾頁只有一行「小計」。要求是**兩者完全一致**:
 * 抬頭、表身欄位、尾頁合計區三者都一樣。
 *
 * 所以這裡把已經查回來的同一份資料依客戶重排,**每一位客戶各渲染一份與單一客戶版
 * 一模一樣的三件套**:
 *  1. 抬頭 —— `ArDetailPrintHeader`(透過 `ArDetailPrintPages`,每頁各一份)
 *  2. 表身 —— `arDetailColumns` 那十欄(REQ-20260831002 / REQ-20260831009)
 *  3. 尾頁合計區 —— `ArDetailTotalsSummary` 的七項(REQ-20260901012)
 *
 * **三件套一份都沒有複製**,全部是 `ArDetailItemTable` 那條路徑上的既有元件 ——
 * 複製一份欄位定義或合計算法,下一次改單一客戶版時這裡就會默默分岔。
 *
 * REQ-20260902001 把**螢幕**那張卡片的抬頭也收進同一條路:原本卡片自己有一套
 * 「客戶簡稱 + 編號」加八格雙方基本資料,與紙上那份對不上。現在改用同一個
 * `ArDetailPrintHeader`(`mode="screen"`),抬頭也不再有第二份 markup。
 *
 * ⚠️ **這支只給 `ar-detail` 用。** 業務員應收帳款明細(`ar-by-person`)的分組列印
 * 仍走 `GroupedCustomerStatements`(七欄 + 小計),那張表使用者沒提,一行都沒動 ——
 * 兩張表的分組版面從本次起**刻意長得不一樣**,理由寫在 docs/REQ-20260901013 §四。
 *
 * 三個沿用的既有決策:
 *  - **頁數分母每位客戶各自從 1 起算**(圖四就是 `1 / 1`)。那是
 *    `ArDetailPrintPages` 每個 instance 自己裝箱的自然結果,沒有另外做全域頁碼 ——
 *    一份請款單交給一位客戶,分母講的是「你這一份有幾張」。
 *  - **客戶之間換頁靠 `.print-customer-block`(`break-before`),第一位不掛**;
 *    改成在最後一頁掛 `break-after` 會多印一張空白頁(REQ-20260815012)。
 *  - **客戶順序 = 各客戶在原清單第一次出現的順序**(Map 保序),不重新排序:
 *    畫面上看到的順序與印出來的順序一致才不會讓人以為查到的是別份資料。
 */
import type { ArApDetailRow } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { EMPTY_HINT, arDetailColumns, toItemRows } from './ArDetailItemTable';
import { ArDetailPrintHeader, type ArDetailHeaderParty } from './ArDetailPrintHeader';
import { ArDetailPrintPages } from './ArDetailPrintPages';
import { ArDetailTotalsSummary } from './ArDetailTotalsSummary';
import { ReportTable } from './ReportTable';

/** 抬頭要的四項裡,分組時由呼叫端給的三項 —— 客戶那一項每位各自從明細列上取 */
export interface GroupedArDetailHeaderInfo {
  reportLabel: string;
  /** 兩者都是 `<input type="date">` 的值(`YYYY-MM-DD`),留白 = 不限 */
  from: string;
  to: string;
}

export function GroupedArDetailStatements({
  rows,
  reportLabel,
  from,
  to,
}: { rows: ArApDetailRow[] } & GroupedArDetailHeaderInfo): JSX.Element {
  const groups = groupByParty(rows);

  return (
    <div className="grid gap-6" data-testid="grouped-customer-statements">
      {groups.map((g, i) => (
        <CustomerStatement
          key={g[0].partyId}
          rows={g}
          first={i === 0}
          reportLabel={reportLabel}
          from={from}
          to={to}
        />
      ))}
    </div>
  );
}

/** 依 `partyId` 分組,順序 = 各客戶在原清單裡第一次出現的順序 */
function groupByParty(rows: ArApDetailRow[]): Array<[ArApDetailRow, ...ArApDetailRow[]]> {
  const map = new Map<number, [ArApDetailRow, ...ArApDetailRow[]]>();
  for (const row of rows) {
    const group = map.get(row.partyId);
    if (group) group.push(row);
    else map.set(row.partyId, [row]);
  }
  return [...map.values()];
}

function CustomerStatement({
  rows,
  first,
  reportLabel,
  from,
  to,
}: { rows: [ArApDetailRow, ...ArApDetailRow[]]; first: boolean } & GroupedArDetailHeaderInfo): JSX.Element {
  const head = rows[0];
  const items = toItemRows(rows);

  /*
   * 該客戶自己的四個數字。**算法與 `ArDetailItemTable` 的合計一字不差**:
   * 金額 = 各品項列加總(含版費 / 刀模費補出來的列)、折讓是單頭欄位逐單加一次、
   * 已收款 / 未收款是各單的 `settledAmount` / `outstanding` 加總。
   *
   * 已收 / 未收在單一客戶版取的是後端算好的 `totalSettled` / `totalOutstanding`,
   * 那是**全部客戶**的總計,分組時當然不能用 —— 改成逐單加總。
   * REQ-20260901012 的兩條算式仍然收斂:後端 `calcOutstanding()` 逐單就是
   * `outstanding = total − settled − allowance`,逐單成立 → 逐客戶加總也成立。
   */
  const totalAmount = items.reduce((sum, r) => sum + Number(r.line.amount), 0);
  const totalAllowance = rows.reduce((sum, r) => sum + Number(r.allowanceAmount ?? 0), 0);
  const totalSettled = rows.reduce((sum, r) => sum + Number(r.settledAmount), 0);
  const totalOutstanding = rows.reduce((sum, r) => sum + Number(r.outstanding), 0);

  const footer: Array<string | null> = [
    '合計',
    null,
    null,
    null,
    null,
    null,
    formatMoney(totalAmount),
    formatMoney(totalAllowance),
    formatMoney(totalSettled),
    formatMoney(totalOutstanding),
  ];

  /*
   * 合計說明區 —— 螢幕與紙上是同一個節點(`ArDetailItemTable` 也是這樣傳)。
   * 元件本身沒有改,只是兩個總計吃該客戶自己的數字。
   */
  const summary = (
    <ArDetailTotalsSummary
      rows={rows}
      lines={items.map((r) => r.line)}
      totalSettled={String(totalSettled)}
      totalOutstanding={String(totalOutstanding)}
    />
  );

  /*
   * 抬頭的六格從明細列上取(REQ-20260901013 為此在後端多帶了 `partyFullName` 與
   * `partyPaymentCert`)。客戶名稱要的是**全名**,退回 `partyName`(簡稱)只是
   * 舊資料沒有全名時的退路。任一格為空時 `ArDetailPrintHeader` 印「—」,不擋住列印。
   */
  const party: ArDetailHeaderParty = {
    code: head.partyCode,
    fullName: head.partyFullName ?? head.partyName,
    contact: head.partyContact ?? null,
    phone: head.partyPhone ?? null,
    taxId: head.partyTaxId ?? null,
    paymentCert: head.partyPaymentCert ?? null,
  };

  return (
    // 第一個區塊不加 .print-customer-block,否則列印時會先吐一張空白頁
    <section
      className={first ? '' : 'print-customer-block'}
      data-testid="customer-block"
    >
      {/*
        螢幕那份。**整塊 `print:hidden`** —— 紙上只留 `ArDetailPrintPages` 那一份,
        版面分家的做法照 `ArDetailItemTable` 的 `ar-detail-screen-table`。
      */}
      <div
        className="bg-white shadow-sm rounded-md p-4 print:hidden"
        data-testid="customer-screen-card"
      >
        <header className="border-b pb-2">
          {/* 螢幕專用的一行:紙上不需要(一份請款單就是這位客戶的全部,
              而且抬頭已經有頁數分母),所以不放進共用抬頭的三欄裡 */}
          <div className="text-right text-xs text-slate-500">{rows.length} 張單據</div>
          {/*
            抬頭與紙上那份是**同一個元件**(REQ-20260902001)。原本這裡是自己一套
            「客戶簡稱 + 編號」加八格雙方基本資料,與列印那份對不上 ——
            使用者要的就是兩者一致。螢幕模式不印頁數(這份沒有分頁),
            兩格地址也跟著不見:抬頭六格裡本來就沒有地址。
          */}
          <ArDetailPrintHeader
            mode="screen"
            reportLabel={reportLabel}
            customer={party}
            from={from}
            to={to}
          />
        </header>

        <div className="mt-2">
          {/* 表頭的四個工具類與 `ArDetailItemTable` 螢幕那份完全相同,不另寫一套 */}
          <ReportTable
            columns={arDetailColumns}
            rows={items}
            rowKey={(r) => r.line.key}
            headClassName="[&_th]:text-xs [&_th]:font-normal [&_th]:whitespace-nowrap [&_th]:px-2"
            isLoading={false}
            isError={false}
            error={undefined}
            emptyHint={EMPTY_HINT}
            footer={footer}
          />
        </div>
        {summary}
      </div>

      {/*
        紙上那份。一位客戶一個 instance,所以每位各自裝箱、頁數分母各自從 1 起算。
        三態不會發生(這支只在有已交貨的列時才被渲染),但 props 是必填,照傳。
      */}
      <ArDetailPrintPages
        columns={arDetailColumns}
        rows={items}
        rowKey={(r) => r.line.key}
        footer={footer}
        isLoading={false}
        isError={false}
        error={undefined}
        emptyHint={EMPTY_HINT}
        // 日期收窄之後省下的左右空間全歸商品名稱(REQ-20260901003 第 5 點)
        growColumnKey="productName"
        // 只印在最後一頁,高度會被算進裝箱(REQ-20260901012)
        summary={summary}
        reportLabel={reportLabel}
        customer={party}
        from={from}
        to={to}
      />
    </section>
  );
}
